
import React, { createContext, useState, ReactNode } from 'react';
import { BookingContextType, BookingLink } from '../types/booking';
import { createDefaultBookingLink } from '../utils/bookingDefaults';

// Create the context
export const BookingContext = createContext<BookingContextType | undefined>(undefined);

// Provider component
export const BookingProvider = ({ children }: { children: ReactNode }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [currentBookingLink, setCurrentBookingLink] = useState<BookingLink | null>(
    createDefaultBookingLink()
  );

  const updateMeetingBasics = (name: string, duration: 15 | 30 | 60) => {
    if (currentBookingLink) {
      setCurrentBookingLink({
        ...currentBookingLink,
        name,
        duration,
      });
    }
  };

  const updateAvailability = (
    day: string,
    updates: { enabled?: boolean; startTime?: string; endTime?: string }
  ) => {
    if (currentBookingLink) {
      setCurrentBookingLink({
        ...currentBookingLink,
        availability: {
          ...currentBookingLink.availability,
          [day]: {
            ...currentBookingLink.availability[day],
            ...updates,
          },
        },
      });
    }
  };

  const updateMeetingTypes = (types: Partial<BookingLink['meetingTypes']>) => {
    if (currentBookingLink) {
      setCurrentBookingLink({
        ...currentBookingLink,
        meetingTypes: {
          ...currentBookingLink.meetingTypes,
          ...types,
        },
      });
    }
  };

  const updatePayment = (required: boolean, amount: number = 0) => {
    if (currentBookingLink) {
      setCurrentBookingLink({
        ...currentBookingLink,
        payment: {
          required,
          amount: required ? amount : 0,
        },
      });
    }
  };
  
  const updateTimezone = (timezone: string) => {
    if (currentBookingLink) {
      setCurrentBookingLink({
        ...currentBookingLink,
        timezone,
      });
    }
  };
  
  const updateLocations = (locations: string[]) => {
    if (currentBookingLink) {
      setCurrentBookingLink({
        ...currentBookingLink,
        locations,
      });
    }
  };
  
  const updateRecurring = (recurring: BookingLink['recurring']) => {
    if (currentBookingLink) {
      setCurrentBookingLink({
        ...currentBookingLink,
        recurring,
      });
    }
  };

  const updateProviderInfo = (providerInfo: Partial<BookingLink['providerInfo']>) => {
    if (currentBookingLink && currentBookingLink.providerInfo) {
      setCurrentBookingLink({
        ...currentBookingLink,
        providerInfo: {
          ...currentBookingLink.providerInfo,
          ...providerInfo,
        },
      });
    }
  };

  const updateServiceDetails = (serviceDetails: Partial<BookingLink['serviceDetails']>) => {
    if (currentBookingLink && currentBookingLink.serviceDetails) {
      setCurrentBookingLink({
        ...currentBookingLink,
        serviceDetails: {
          ...currentBookingLink.serviceDetails,
          ...serviceDetails,
        },
      });
    }
  };

  const resetBookingLink = () => {
    setCurrentBookingLink(createDefaultBookingLink());
    setCurrentStep(1);
  };

  return (
    <BookingContext.Provider
      value={{
        currentBookingLink,
        currentStep,
        setCurrentStep,
        updateMeetingBasics,
        updateAvailability,
        updateMeetingTypes,
        updatePayment,
        updateTimezone,
        updateLocations,
        updateRecurring,
        updateProviderInfo,
        updateServiceDetails,
        resetBookingLink,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

// Re-export the useBooking hook to maintain backwards compatibility
export { useBooking } from '../hooks/useBookingContext';
